<template>
    <div>
        <div class="grid grid-cols-1 gap-4 mt-12 md:grid-cols-3">
            <div class="py-4 md:pr-8">
                <span class="block mb-4 text-2xl font-secondary">{{ __('Twoje dane') }}</span>
                <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">

                    <span class="block mb-4 font-light lg:col-span-2" v-if="!isClientAuthenticated">
                        {{ __('Masz już konto?') }}
                        <a :href="route('front.login.view')" class="text-orange">{{ __('Zaloguj się') }}</a>
                    </span>

                    <input-text
                        v-model="client.first_name"
                        name="client.first_name"
                        :label="__('Imię')"
                        :errors="errors"
                    />
                    <input-text
                        v-model="client.last_name"
                        name="client.last_name"
                        :label="__('Nazwisko')"
                        :errors="errors"
                    />
                    <input-text
                        v-model="client.email"
                        name="client.email"
                        :label="__('Adres e-mail')"
                        :errors="errors"
                    />
                    <input-text
                        v-model="client.email_repeat"
                        name="client.email_repeat"
                        :label="__('Powtórz adres e-mail')"
                        :errors="errors"
                    />
                    <div>
                        <input-text
                            v-model="client.phone"
                            name="client.phone"
                            :label="__('Nr telefonu')"
                            :errors="errors"
                        />
                        <small v-if="phoneAlertMessage" class="validation-error">
                            {{ phoneAlertMessage }}
                        </small>
                    </div>
                </div>

                <div v-if="isDeliveryAvailable && ! requirePickupPoint" class="grid grid-cols-1 gap-3 mt-3 sm:grid-cols-2">
                    <input-text
                        v-model="address.street"
                        name="address.street"
                        :label="__('Ulica')"
                        :errors="errors"
                    />

                    <input-text
                        v-model="address.house_number"
                        name="address.house_number"
                        :label="__('Nr domu/lokalu')"
                        :errors="errors"
                    />

                    <input-text
                        v-model="address.postcode"
                        name="address.postcode"
                        :label="__('Kod pocztowy')"
                        :errors="errors"
                    />

                    <input-text
                        v-model="address.city"
                        name="address.city"
                        class='placeholder-grey-200'
                        :label="__('Miasto')"
                        :errors="errors"
                    />
                </div>

                <div class="grid grid-cols-1 gap-3 mt-3 sm:grid-cols-2">
                    <input-checkbox
                        v-if="!isClientAuthenticated"
                        name="register_account"
                        class="mt-8 sm:col-span-2"
                        v-model="registration.register"
                        :label="__('Użyj powyższych danych i utwórz konto')"
                        :errors="errors"
                    />

                    <template v-if="registration.register">
                        <input-text
                            v-model="registration.password"
                            class="sm:col-span-2"
                            type="password"
                            name="registration.password"
                            :label="__('Hasło (min. 8 znaków, litery i cyfry)')"
                            :errors="errors"
                        />
                        <input-text
                            v-model="registration.password_confirmation"
                            class="sm:col-span-2"
                            type="password"
                            name="registration.password_confirmation"
                            :label="__('Powtórz hasło')"
                            :errors="errors"
                        />
                    </template>
                </div>
            </div>

            <div class="py-4 md:col-span-2">
                <div class="grid grid-cols-1 gap-0 md:grid-cols-2 md:border-l border-grey-200 high-contrast:border-yellow-300">
                    <div class="py-4 md:px-6 md:border-r border-grey-200 high-contrast:border-yellow-300">
                        <span class="block mb-4 text-2xl font-secondary">
                            {{ __('Sposób dostawy') }}
                            <sup class="text-xs uppercase text-orange" v-if="hasFreeShipping">{{ __('Darmowa dostawa') }}</sup>
                        </span>
                        <span v-if="! isDeliveryAvailable">
                            {{ __('Dostawa nie jest dostępna dla zamówionych produktów') }}
                        </span>

                        <div v-if="canSelectTicketsDeliveryMethod" class="mb-4">
                            <input-radio
                                v-model="hasSelectedTicketDelivery"
                                class="mt-8 sm:col-span-2"
                                name="false"
                                :label="__('Bilet kolekcjonerski otrzymasz przy wejściu na koncert')"
                            />

                            <input-radio
                                v-if="isPolishLangVersion"
                                v-model="hasSelectedTicketDelivery"
                                class="mt-4 sm:col-span-2"
                                name="true"
                                :label="__('Bilet kolekcjonerski wyślemy na wskazany adres')"
                            />

                            <div class="flex mt-4 sm:col-span-2">
                                <span class="w-4 mt-1 mr-4"><warning class="w-4 fill-orange"/></span>
                                <span class="text-base font-light text-orange">
                                    {{ __('Aby wejść na koncert będzie Ci potrzebny bilet elektroniczny z kodem kreskowym. Otrzymasz go na skrzynkę mailową po opłaceniu zamówienia.') }}
                                </span>
                            </div>
                        </div>

                        <div v-if="isDeliveryAvailable">
                            <span class="block col-span-2 text-black mb-2 high-contrast:text-yellow-300">
                                {{ __('Dostawa:') }}
                            </span>

                            <input-radio
                                v-for="(type, index) in deliveryTypes"
                                class="mb-4"
                                :key="`delivery-types-${index}`"
                                v-model="selectedDeliveryTypeId"
                                :name="type.id"
                                :label="`${type.name} (${$options.filters.price(type.price)})`"
                            />
                        </div>

                        <div v-if="isDeliveryAvailable && ! requirePickupPoint" class="grid grid-cols-1 gap-3 mt-3 sm:grid-cols-2">
                            <div class="sm:col-span-2">
                                <div class="grid grid-cols-3 gap-3">
                                    <div class="col-span-2">
                                        <input-text
                                            v-model="address.street"
                                            name="address.street"
                                            :label="__('Ulica')"
                                            :errors="errors"
                                        />
                                    </div>
                                    <input-text
                                        v-model="address.house_number"
                                        name="address.house_number"
                                        :label="__('Nr domu')"
                                        :errors="errors"
                                    />
                                </div>
                            </div>
                            <input-text
                                v-model="address.postcode"
                                name="address.postcode"
                                :label="__('Kod pocztowy')"
                                :errors="errors"
                            />

                            <input-text
                                v-model="address.city"
                                name="address.city"
                                class='placeholder-grey-200'
                                :label="__('Miasto')"
                                :errors="errors"
                            />
                        </div>

                        <div>
                            <template v-if="hasSelectedPickupPoint">
                                <span class="block">
                                    {{ __('Twój wybrany punkt odbioru') }}:
                                </span>
                                <span class="block text-black high-contrast:text-yellow-300">
                                    {{ pickupPointFullName }}
                                </span>
                            </template>

                            <template v-if="requirePickupPoint">
                                <span
                                    v-for="(error, index) in pickupPointErrors"
                                    v-bind:key="`error-${index}`"
                                    class="validation-error"
                                >
                                        {{ error }}
                                </span>

                                <a href="javascript:;" @click="isPopupOpen = true" class="block mt-1 font-light underline text-orange">
                                    {{ __('Wybierz punkt odbioru') }}
                                </a>
                            </template>
                        </div>

                        <popup
                            v-if="isPopupOpen"
                            @close="isPopupOpen = false"
                            title="Wybierz punkt odbioru"
                        >
                            <in-post-widget
                                @select-point="selectPickupPoint"
                            />
                        </popup>
                    </div>

                    <div class="py-4 md:pl-6">
                        <span class="block mb-4 text-2xl font-secondary">{{ __('Płatność') }}</span>

                        <input-radio
                            v-for="(paymentType, index) in paymentTypes"
                            class="mb-4"
                            :key="`payment-types-${index}`"
                            v-model="payment"
                            :name="paymentType.key"
                            :label="paymentType.label"
                        />

                        <hr class="mt-4 mb-5 border-grey-200 high-contrast:border-yellow-300"/>

                        <input-checkbox
                            name="show_invoice_form"
                            class="mb-2"
                            v-model="invoice.show_form"
                            :label="__('Wystaw fakturę VAT')"
                            :errors="errors"
                        />
                        <div class="grid grid-cols-1 gap-3 lg:grid-cols-2" v-if="invoice.show_form">
                            <div class="lg:col-span-2">
                                <input-radio
                                    class="mt-4"
                                    v-model="selectedInvoiceType"
                                    :name="invoiceTypes.PER_PRIVATE_PERSON"
                                    :label="__('na osobę prywatną')"
                                />
                                <input-radio
                                    class="mt-4"
                                    v-model="selectedInvoiceType"
                                    :name="invoiceTypes.PER_COMPANY"
                                    :label="__('na firmę')"
                                />
                            </div>
                            <div class="lg:col-span-2">
                                <input-text
                                    v-model="invoice.name"
                                    name="invoice.name"
                                    :label="isInvoiceForCompany ? __('Nazwa firmy') : __('Imię i nazwisko')"
                                    :errors="errors"
                                />
                            </div>
                            <div class="lg:col-span-2" v-if="isInvoiceForCompany">
                                <input-text
                                    v-model="invoice.nip"
                                    name="invoice.nip"
                                    :label="__('NIP')"
                                    :errors="errors"
                                />
                            </div>
                            <div class="lg:col-span-2">
                                <div class="grid grid-cols-3 gap-3">
                                    <div class="col-span-2">
                                        <input-text
                                            v-model="invoice.street"
                                            name="invoice.street"
                                            :label="__('Ulica')"
                                            :errors="errors"
                                        />
                                    </div>

                                    <input-text
                                        v-model="invoice.house_number"
                                        name="invoice.house_number"
                                        :label="__('Nr domu')"
                                        :errors="errors"
                                    />
                                </div>
                            </div>

                            <input-text
                                v-model="invoice.postcode"
                                name="invoice.postcode"
                                :label="__('Kod pocztowy')"
                                :errors="errors"
                            />

                            <input-text
                                v-model="invoice.city"
                                name="invoice.city"
                                :label="__('Miasto')"
                                :errors="errors"
                            />

                        </div>
                    </div>
                </div>

                <div class="w-100">
                    <hr class="my-3 border-grey-200 high-contrast:border-yellow-300"/>
                </div>

                <div class="py-4 md:pl-6 md:border-l border-grey-200 high-contrast:border-yellow-300">
                    <input-textarea
                        class="my-4"
                        v-model="clientComments"
                        name="client_comments"
                        :label="__('Uwagi do zamówienia')"
                        :errors="errors"
                    />
                </div>
            </div>
        </div>

        <div class="grid grid-cols-1 gap-4 my-6">
            <input-checkbox
                v-if="! isClientAuthenticated"
                name="consents.regulations"
                class="mt-8"
                v-model="consents.regulations"
                label=""
                :HTMLcontent="consentsContent.regulations"
                :errors="errors"
            />
            <input-checkbox
                v-if="! isClientAuthenticated"
                name="consents.privacy"
                v-model="consents.privacy"
                label=""
                :HTMLcontent="consentsContent.privacy"
                :errors="errors"
            />
            <input-checkbox
                v-if="hasAnyTicketOrdered"
                name="consents.event"
                v-model="consents.event"
                label=""
                :HTMLcontent="consentsContent.event"
                :errors="errors"
            />
            <input-checkbox
                v-if="! isClientAuthenticated || ! client.is_newsletter_subscriber"
                name="consents.newsletter"
                v-model="consents.newsletter"
                label=""
                :HTMLcontent="consentsContent.newsletter"
                :errors="errors"
            />
        </div>

        <div class="flex flex-col-reverse justify-between py-8 text-center lg:flex-row">
            <button
                v-on:click="$emit('back-to-prev-step')"
                class="flex items-center justify-center block w-full px-4 py-2 my-2 text-xl text-white rounded lg:my-8 bg-grey hover:bg-orange lg:w-1/3 font-secondary  high-contrast:text-yellow-300"
            >
                {{ __('Wróć do poprzedniego kroku') }}
            </button>
            <button
                class="flex items-center justify-center block w-full px-4 py-2 my-2 text-xl text-white rounded lg:my-8 bg-orange lg:w-1/3 font-secondary high-contrast:bg-yellow-300 high-contrast:text-black"
                @click="goToNextStep"
            >
                <span>{{ __('Przejdź do podsumowania zamówienia') }}</span>
                <arrow-right class="inline w-4 ml-4 fill-white high-contrast:fill-black"></arrow-right>
            </button>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import InputText from "@/front/components/Inputs/Text";
import InputCheckbox from "@/front/components/Inputs/Checkbox";
import InputRadio from "@/front/components/Inputs/Radio";
import InputTextarea from "@/front/components/Inputs/Textarea";
import Warning from '@/front/components/Icons/Warning';
import ArrowRight from '@/front/components/Icons/ArrowRight';
import Popup from "@/common/components/Popup";
import InPostWidget from "@/front/components/InPostWidget";

const INVOICE_TYPES = {
    PER_COMPANY: 'company',
    PER_PRIVATE_PERSON: 'private'
};

export default {
    props: {
        client: Object,
        payment: String,
        address: Object,
        invoice: Object,
        registration: Object,
        deliveryType: Object,
        pickupPoint: Object,
        hasOnlyTicketsOrdered: Boolean,
        hasOnlyRecordsOrdered: Boolean,
        hasOnlyGiftCardsOrdered: Boolean,
        hasAnyTicketOrdered: Boolean,
        hasAnyRecordOrdered: Boolean,
        hasAnyGiftCardOrdered: Boolean,
        wasSendingTicketsSelected: Boolean,
        consents: Object,
        consentsContent: Object,
        clientComments: String,
        hasFreeShipping: Boolean,
        canSelectTicketsDeliveryMethod: Boolean
    },
    components: {
        Popup,
        InputText,
        InputCheckbox,
        InputTextarea,
        InputRadio,
        Warning,
        ArrowRight,
        InPostWidget
    },
    data() {
        return {
            errors: {},
            isClientAuthenticated: window.Laravel.isClientAuthenticated,
            isPopupOpen: false,
            selectedDeliveryTypeId: null,
            deliveryTypes: [],
            sendTickets: this.wasSendingTicketsSelected,
            paymentTypes: window.Laravel.paymentTypes,
            hasSelectedTicketDelivery: 'false',
            invoiceTypes: INVOICE_TYPES,
            selectedInvoiceType: INVOICE_TYPES.PER_PRIVATE_PERSON,
            isPolishLangVersion: window.Laravel.locale == 'pl'
        };
    },
    computed: {
        selectedDeliveryType() {
            if(this.deliveryTypes.length == 0) {
                return null;
            }

            let deliveryTypeId = this.selectedDeliveryTypeId;
            let deliveryType = _.find(this.deliveryTypes, function(deliveryType) {
                return deliveryType.id == deliveryTypeId;
            });

            return deliveryType;
        },
        isDeliveryAvailable() {
            return this.deliveryTypes.length > 0
                && (this.hasAnyRecordOrdered || (this.hasAnyTicketOrdered && this.sendTickets && this.canSelectTicketsDeliveryMethod));
        },
        isDeliveryRequired() {
            if(this.hasAnyRecordOrdered || (this.hasAnyTicketOrdered && this.sendTickets && this.canSelectTicketsDeliveryMethod)) {
                return true;
            }

            return false;
        },
        hasSelectedPickupPoint() {
            return this.requirePickupPoint && ! _.isEmpty(this.pickupPoint);
        },
        requirePickupPoint() {
            return _.get(this.selectedDeliveryType, 'require_pickup_point', false) && this.isDeliveryRequired;
        },
        pickupPointFullName() {
            let address = this.pickupPoint.address.line1;

            if(this.pickupPoint.address.line2.length) {
                address += (", " + this.pickupPoint.address.line2)
            }

            return address;
        },
        pickupPointErrors() {
            return _.get(this.errors, 'pickup_point_code', []);
        },
        isInvoiceForCompany() {
            return this.selectedInvoiceType == this.invoiceTypes.PER_COMPANY;
        },
        phoneAlertMessage() {
            let message = '';

            if(! this.isPolishLangVersion) {
                return message;
            }

            if(_.isString(this.client.phone) && this.client.phone.startsWith('0')) {
                message +=  'Czy to na pewno prawidłowy numer telefonu? Numer nie powinien zaczynać się od 0.';
            }

            if(_.isString(this.client.phone) && this.client.phone.length > 9) {
                message += _.isEmpty(message)
                    ? 'Czy to na pewno prawidłowy numer telefonu? Numer nie powinien być dłuższy niż 9 znaków.'
                    : ' Numer nie powinien być dłuższy niż 9 znaków.';
            }

            return message;
        }
    },
    mounted() {
        this.loadDeliveryTypes();
    },
    methods: {
        loadDeliveryTypes() {
            axios
                .get(route('json.dictionaries.delivery_types'))
                .then(response => {
                    this.deliveryTypes = _.get(response, "data.data", []);
                    this.selectDeliveryType();
                })
                .catch(error => {
                    console.log(error);
                });
        },
        selectDeliveryType() {
            if(! _.isEmpty(this.deliveryType)) {
                this.selectedDeliveryTypeId = this.deliveryType.id;
                return;
            }

            if(this.deliveryTypes.length > 0) {
                this.selectedDeliveryTypeId = this.deliveryTypes[0].id;
                return;
            }
        },
        goToNextStep() {
            this.$emit('toggle-preloader');

            let data = {
                client: this.client,
                payment: this.payment,
                address: this.address,
                invoice: this.invoice,
                registration: this.registration,
                isDeliveryRequired: this.isDeliveryRequired,
                delivery_type_id: this.isDeliveryRequired ? this.selectedDeliveryType.id : null,
                deliveryType: this.isDeliveryRequired ? this.selectedDeliveryType : null,
                pickupPoint: this.pickupPoint,
                pickup_point_code: _.get(this.pickupPoint, 'name', ""),
                pickup_point_details: _.get(this.pickupPoint, 'address_details', []),
                consents: this.consents,
                is_invoice_for_company: this.isInvoiceForCompany,
                requirePickupPoint: this.requirePickupPoint,
                client_comments: this.clientComments,
                hasOnlyRecordsOrdered: this.hasOnlyRecordsOrdered,
                hasAnyTicketOrdered: this.hasAnyTicketOrdered,
            };

            axios.post(route('json.cart.validate'), data)
                .then(response => {
                    this.errors = {};
                    this.$emit('go-to-summary', data);
                })
                .catch(error => {
                    if (error.response.status == 422) {
                        this.errors = _.get(error.response, "data.errors", {});
                        console.log('ERRR: ',error.response.data);
                    }
                })
                .then(() => {
                    this.$emit('toggle-preloader');
                });
        },
        selectPickupPoint(point) {
            this.$emit('select-pickup-point', point);
            this.isPopupOpen = false;
        },
    },
    watch: {
        selectedDeliveryTypeId: {
            handler(val){
                if(val && this.selectedDeliveryType.require_pickup_point) {
                    if(_.isEmpty(this.pickupPoint)) {
                        this.isPopupOpen = true;
                    }
                } else {
                    this.selectPickupPoint(null);
                }
            }
        },
        hasSelectedTicketDelivery: {
            handler(val){
                return this.sendTickets = (val === 'true');
            }
        }
    }
};
</script>
